import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {Button, FormControl, MenuItem, Select, Typography} from '@material-ui/core';
import {
  changeEnrollmentFile,
  uploadApplication,
  CustomerConnectionProfile,
  clearApplication,
} from 'Agent/Accounts/ducks/a360EnrollmentApplication';
import {
  EnrollmentApplication,
  EnrollmentApplicationWithError,
  EnrollmentApplicationWithDuplicateUploadError,
  InvalidEnrollmentApplication,
  ValidEnrollmentApplication,
  isValidApplication,
} from 'Agent/Accounts/models/A360EnrollmentApplication';

import Alert from 'Shared/components/Alert';
import FileDropzone from 'Shared/components/FileDropzone';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import 'Agent/Accounts/components/A360EnrollmentApplication/EnrollmentApplicationModal.sass';

const isValidFileName = (fileName: string, fileFormat: string): boolean => {
  const regex = correctRegex(fileFormat);
  return regex.test(fileName);
};

const correctRegex = (input: string) => {
  let flags = '';
  // Extract (?i) and replace as regex flag
  if (input.includes('(?i)')) {
    flags += 'i';
    input = input.replace(/\(\?i\)/g, '');
  }
  return new RegExp(input, flags);
}

type A360EnrollmentApplicationFormProps = WithSelectedAgencyProps & {
  application: EnrollmentApplication;
  accountNumber: string;
  accountName: string;
  customerConnectionProfile: CustomerConnectionProfile[];
  onCancel: () => void;
};

export const A360EnrollmentApplicationForm = (props: A360EnrollmentApplicationFormProps) => {
  const dispatch = useDispatch();
  const [isFileValid, setIsFileValid] = useState(true);
  const {enable_portals_virus_scan, SIC_FileNet_Connectivity} = useSelector((state: ReducerState) => state.featureFlag);
  const {application, onCancel, accountNumber, accountName, customerConnectionProfile} = props;
  const [selectedProfile, setSelectedProfile] = useState<CustomerConnectionProfile | undefined>(undefined);

  return (
    <form className="upload-a360-enrollment-application">
      <p className="upload-a360-enrollment-application__intro-text"><b>Please provide the correct enrollment type for the application below:</b></p>
      <Typography variant="body2" className="upload-a360-enrollment-application__dropdown-label">Connection Profile Number</Typography>
      <FormControl variant="outlined" margin="dense" className="upload-a360-enrollment-application__enrollment-type-select">
        <Select
          variant="outlined"
          displayEmpty
          value={selectedProfile?.connectionProfileNumber ?? ''}
          onChange={e => {
            const profile = customerConnectionProfile.find(p => p.connectionProfileNumber === e.target.value);
            setSelectedProfile(profile);
            dispatch(changeEnrollmentFile(undefined));
            setIsFileValid(true);
          }}
        >
          <MenuItem value=""><em>Select a profile number</em></MenuItem>
          {customerConnectionProfile.map(profile => (
            <MenuItem key={profile.connectionProfileNumber} value={profile.connectionProfileNumber}>
              {profile.connectionProfileNumber}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedProfile && (
        <div className="upload-a360-enrollment-application__profile-info">
          <div className="upload-a360-enrollment-application__profile-columns">
            <div>
              <Typography variant="body2">Tech Partner:</Typography>
              <Typography variant="body2" className="upload-a360-enrollment-application__profile-tech-partner"><strong>{selectedProfile.techPartnerName}</strong></Typography>
            </div>
            <div>
              <Typography variant="body2">Customer/Group Sponsor(s):</Typography>
              <Typography variant="body2" className="upload-a360-enrollment-application__profile-sponsor-value"><strong>{accountName}</strong></Typography>
            </div>
          </div>
          <Typography variant="body2" className="upload-a360-enrollment-application__profile-upload-instruction"><strong>Upload your enrollment file in .xls or .xlsx format following the naming convention below.</strong></Typography>

          <Typography variant="body2" className="upload-a360-enrollment-application__profile-filename-label">Filename format:</Typography>
          <Typography variant="body2" className="upload-a360-enrollment-application__profile-filename-value"><strong>"{selectedProfile.fileNameConfiguration[0]?.fileName?.replace(/^[\(^]+/, '').replace(/\)\.\*\)*$/, '.*')}"</strong></Typography>
          <Typography variant="body2" className="upload-a360-enrollment-application__profile-spacer" />
        </div>
      )}
      <div className="upload-a360-enrollment-application__file-dropzone-wrapper">
        <FileDropzone
          className="upload-a360-enrollment-application__file-dropzone"
          file={application.file}
          onSelectFile={file => {
            if (!selectedProfile) return;
            // validate filename against file format received
            const isOkay = isValidFileName(file.name, selectedProfile.fileNameConfiguration[0].fileName);
            dispatch(changeEnrollmentFile(isOkay ? file : undefined));
            setIsFileValid(isOkay);
          }}
          useCase='A360_ENROLLMENT_FILE'
        />
      </div>

      {!isFileValid && (
        <Alert message="Filename format is incorrect." />
      )}
      {application instanceof EnrollmentApplicationWithDuplicateUploadError && (
        <Alert message="A file with same name has already been uploaded" />
      )}
      {application instanceof EnrollmentApplicationWithError && (
        <Alert message="An error occurred while saving the enrollment application" />
      )}
      <div className="upload-a360-enrollment-application__enrollment-actions">
        <Button
          data-testid="upload-application-button"
          className="upload-a360-enrollment-application__submit-button"
          variant="contained"
          disabled={!selectedProfile || !isValidApplication(application)}
          type="submit"
          color="primary"
          onClick={() => {
            if (application instanceof ValidEnrollmentApplication && selectedProfile) {
              dispatch(
                uploadApplication(
                  application,
                  enable_portals_virus_scan,
                  accountNumber, 
                  selectedProfile.connectionProfileNumber,
                  SIC_FileNet_Connectivity
                )
              );
            }
          }}
        >
          Submit
        </Button>
        <Button variant="contained" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default withSelectedAgency(A360EnrollmentApplicationForm);
